// src/pages/QrScanPage.js

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
    FaCar, FaPhone, FaLink, FaCopy, FaCheck,
    FaSpinner, FaExclamationTriangle, FaQuestionCircle, FaQrcode, FaBuilding,
    FaUser, FaInstagram, FaWhatsapp, FaEnvelope, FaPaperPlane, FaGlobe, FaShieldAlt
} from 'react-icons/fa';
import { resolveContactField } from '../lib/phoneUtils';
import { useAuth } from '../context/AuthContext';
import QrDirectChat from '../components/QrDirectChat';

//==============================================================================
// HELPER FUNCTIONS & CUSTOM HOOKS
//==============================================================================

/**
 * Masks a name for privacy (e.g., "Ahmet Yılmaz" -> "Ah*** Y.").
 */
const maskName = (name) => {
    if (!name || typeof name !== 'string' || name.trim().length < 2) return 'Araç Sahibi';
    const parts = name.trim().split(/\s+/);
    const firstName = parts[0];
    const lastNameInitial = parts.length > 1 ? parts[parts.length - 1][0] : '';
    const maskedFirstName = firstName.substring(0, Math.min(2, firstName.length)) + '***';
    return `${maskedFirstName}${lastNameInitial ? ' ' + lastNameInitial.toUpperCase() + '.' : ''}`;
};

/**
 * Formats a phone number for a 'tel:' link.
 */
const formatPhoneLink = (phone) => phone ? `tel:${phone.replace(/[\s()-]/g, '')}` : '#';

/**
 * Formats external URL with https:// if protocol is missing
 */
const formatWebLink = (url) => {
    if (!url) return '#';
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

/**
 * Custom hook to handle copying text to clipboard with feedback
 */
const useCopyToClipboard = (timeout = 1600) => {
    const [copiedKey, setCopiedKey] = React.useState(null);

    const copy = React.useCallback(async (textToCopy, key) => {
        if (!navigator?.clipboard || !textToCopy) return;
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), timeout);
        } catch (error) {
            console.warn('Clipboard copy failed:', error);
            setCopiedKey(null);
        }
    }, [timeout]);

    return [copiedKey, copy];
};

//==============================================================================
// MAIN COMPONENT
//==============================================================================

const QrScanPage = () => {
    const { qrCodeId } = useParams();
    const [pageState, setPageState] = React.useState('loading'); // 'loading', 'profile', 'not_assigned', 'invalid'
    const [profile, setProfile] = React.useState(null); // { type: 'company' | 'personal', data: ... }

    const fetchData = React.useCallback(async () => {
        if (!qrCodeId) {
            setPageState('invalid');
            return;
        }

        // Sanitize ID
        let cleanId = qrCodeId.trim().toUpperCase();
        if (cleanId.endsWith('/')) {
            cleanId = cleanId.slice(0, -1);
        }
        cleanId = cleanId.split('?')[0];

        setPageState('loading');

        try {
            const profileDocRef = doc(db, 'vehicleProfiles', cleanId);
            const profileSnap = await getDoc(profileDocRef);

            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                let fetchedProfile;

                if (profileData.companyId) {
                    // Company Car Profile
                    const companyDocRef = doc(db, 'companies', profileData.companyId);
                    const companySnap = await getDoc(companyDocRef);
                    if (companySnap.exists()) {
                        const companyData = companySnap.data();
                        profileData.companyWebsite = resolveContactField(companyData.companyWebsite);
                        profileData.companyContactPhone = resolveContactField(companyData.companyContactPhone);
                        profileData.companyInstagram = resolveContactField(companyData.companyInstagram);
                        profileData.companyWhatsapp = resolveContactField(companyData.companyWhatsapp);
                    }

                    if (profileData.isRented && profileData.currentRentalId) {
                        const rentalDocRef = doc(db, 'rentals', profileData.currentRentalId);
                        const rentalSnap = await getDoc(rentalDocRef);
                        if (rentalSnap.exists()) {
                            profileData.rental = rentalSnap.data();
                        }
                    }

                    profileData.qrCodeId = cleanId;
                    fetchedProfile = { type: 'company', data: profileData };
                } else {
                    // Personal Vehicle Profile
                    const ci = profileData.contactInfo || {};
                    const resolvedContacts = {
                        phone: resolveContactField(ci.phone),
                        email: resolveContactField(ci.email),
                        telegram: resolveContactField(ci.telegram),
                        website: resolveContactField(ci.website),
                    };

                    const profileWithDefaults = {
                        ...profileData,
                        qrCodeId: cleanId,
                        contactInfo: resolvedContacts,
                    };

                    // Fetch masked owner name from users collection
                    if (profileData.userId) {
                        const userDocRef = doc(db, 'users', profileData.userId);
                        const userSnap = await getDoc(userDocRef);
                        profileWithDefaults.ownerName = userSnap.exists() ? maskName(userSnap.data().displayName) : 'Araç Sahibi';
                    } else {
                        profileWithDefaults.ownerName = 'Araç Sahibi';
                    }

                    fetchedProfile = { type: 'personal', data: profileWithDefaults };
                }

                setProfile(fetchedProfile);
                setPageState('profile');

            } else {
                // Check if the QR code is generated but not yet linked to any profile
                const qrCodeDocRef = doc(db, 'qrCodes', cleanId);
                const qrCodeSnap = await getDoc(qrCodeDocRef);
                if (qrCodeSnap.exists() && qrCodeSnap.data().isLinked === false) {
                    setPageState('not_assigned');
                } else {
                    setPageState('invalid');
                }
            }
        } catch (error) {
            console.error("Error fetching QR scan data:", error);
            setPageState('invalid');
        }
    }, [qrCodeId]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="w-full min-h-[100dvh] bg-[#F5F5F7] flex items-center justify-center p-4 sm:p-6 font-sans">
            <div className="w-full max-w-md bg-white/90 backdrop-blur-xl shadow-[0_12px_40px_rgb(0,0,0,0.08)] border border-black/[0.06] rounded-3xl overflow-hidden transition-all duration-300">
                <Header pageState={pageState} profile={profile} qrCodeId={qrCodeId} />
                <div className="p-6 sm:p-8 min-h-[340px] flex flex-col justify-between">
                    <Content pageState={pageState} profile={profile} qrCodeId={qrCodeId} />
                    
                    {/* Apple-style footer badge */}
                    <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                        <FaShieldAlt className="text-gray-400" size={12} />
                        <span>Kodu Tara Gizlilik Korumalı İletişim</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

//==============================================================================
// UI SUB-COMPONENTS
//==============================================================================

const Header = ({ pageState, profile, qrCodeId }) => {
    const isLoading = pageState === 'loading';
    
    // Company Profile Header
    if (pageState === 'profile' && profile?.type === 'company') {
        const { companyName, vehiclePlate } = profile.data;
        return (
            <div className="bg-gradient-to-b from-gray-900 to-black text-white p-7 text-center flex flex-col justify-center items-center">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-3 backdrop-blur-md border border-white/10">
                    <FaBuilding size={24} className="text-white" />
                </div>
                <h2 className="text-xl font-bold tracking-tight truncate max-w-full">{companyName || 'Rent A Car'}</h2>
                {vehiclePlate && (
                    <span className="mt-1 px-3 py-0.5 rounded-full bg-white/15 text-xs font-mono font-bold tracking-widest text-gray-200 border border-white/10">
                        {vehiclePlate}
                    </span>
                )}
            </div>
        );
    }

    // Personal Profile & Default Header
    const plate = profile?.data?.vehiclePlate;
    const title = pageState === 'profile' ? (plate || qrCodeId) : 'Kodu Tara';
    
    return (
        <div className="bg-gradient-to-b from-gray-950 via-gray-900 to-black text-white p-7 text-center flex flex-col justify-center items-center relative overflow-hidden">
            {/* Subtle glow accent */}
            <div className="absolute -top-12 -left-12 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl pointer-events-none"></div>
            
            {isLoading ? (
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-3">
                    <FaSpinner className="animate-spin text-2xl text-blue-400" />
                </div>
            ) : (
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-3 backdrop-blur-md border border-white/10">
                    {pageState === 'profile' ? <FaCar size={26} className="text-white" /> : <FaQrcode size={26} className="text-white" />}
                </div>
            )}

            <h2 className={`text-xl sm:text-2xl font-extrabold tracking-tight transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                {title}
            </h2>

            {pageState === 'profile' && plate && (
                <span className="mt-1.5 px-3 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-mono font-semibold tracking-wider border border-blue-400/20">
                    Akıllı Araç Numaratörü
                </span>
            )}
        </div>
    );
};

const Content = ({ pageState, profile, qrCodeId }) => {
    const { currentUser } = useAuth();
    const isOwner = currentUser?.uid && profile?.data?.userId === currentUser.uid;

    switch (pageState) {
        case 'loading':
            return (
                <div className="text-center my-auto py-12 text-gray-400 flex flex-col items-center gap-3">
                    <FaSpinner className="animate-spin text-2xl text-gray-400" />
                    <span className="text-sm font-medium">Bilgiler yükleniyor...</span>
                </div>
            );
        case 'profile':
            return profile.type === 'company' 
                ? <CompanyCarView profileData={profile.data} isOwner={Boolean(isOwner)} /> 
                : <PersonalCarView profileData={profile.data} isOwner={Boolean(isOwner)} />;
        case 'not_assigned':
            return (
                <StatusCard icon={FaQuestionCircle} color="amber" title="Numaratör Henüz Tanımlanmamış">
                    <p className="text-sm text-gray-500 mt-2">
                        Bu QR kod henüz herhangi bir araca bağlanmamış.
                    </p>
                    <div className="mt-6">
                        <Link 
                            to={`/link-qr?code=${qrCodeId || ''}`} 
                            className="inline-flex items-center justify-center px-5 py-2.5 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                        >
                            Bu Kodu Hesabınıza Bağlayın
                        </Link>
                    </div>
                </StatusCard>
            );
        default:
            return (
                <StatusCard icon={FaExclamationTriangle} color="red" title="Geçersiz veya Bulunamayan Kod">
                    <p className="text-sm text-gray-500 mt-2">
                        Taratılan QR kod sistemimizde bulunamadı veya bağlantısı sonlandırılmış olabilir.
                    </p>
                    <div className="mt-6">
                        <Link 
                            to="/" 
                            className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-all"
                        >
                            Ana Sayfaya Git
                        </Link>
                    </div>
                </StatusCard>
            );
    }
};

const PersonalCarView = ({ profileData, isOwner = false }) => {
    const { ownerName, visibleNote, contactInfo } = profileData;
    const [copiedKey, copy] = useCopyToClipboard();

    // Check if at least one contact item is visible AND has a valid value
    const visibleContacts = [
        { key: 'phone', label: 'Telefon', icon: FaPhone, info: contactInfo?.phone, link: formatPhoneLink(contactInfo?.phone?.value) },
        { key: 'email', label: 'E-Posta', icon: FaEnvelope, info: contactInfo?.email, link: `mailto:${contactInfo?.email?.value}` },
        { 
            key: 'telegram', 
            label: 'Telegram', 
            icon: FaPaperPlane, 
            info: contactInfo?.telegram, 
            link: `https://t.me/${(contactInfo?.telegram?.value || '').replace('@', '')}` 
        },
        { 
            key: 'website', 
            label: 'Web / İlan', 
            icon: FaGlobe, 
            info: contactInfo?.website, 
            link: formatWebLink(contactInfo?.website?.value) 
        },
    ].filter(item => item.info?.isVisible && item.info?.value);

    return (
        <div className="space-y-6">
            {/* Owner Note */}
            <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Araç Sahibinin Mesajı
                </h3>
                <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200/60 text-gray-800 text-sm leading-relaxed">
                    {visibleNote ? (
                        <p className="font-medium">"{visibleNote}"</p>
                    ) : (
                        <p className="text-gray-400 italic text-xs">Araç sahibi özel bir mesaj bırakmadı.</p>
                    )}
                </div>
            </div>

            {/* Owner Masked Name */}
            <div className="flex items-center justify-between p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-bold text-xs">
                        <FaUser size={12} />
                    </div>
                    <div>
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">Araç Sahibi</span>
                        <span className="text-sm font-bold text-gray-900">{ownerName || 'Araç Sahibi'}</span>
                    </div>
                </div>
                <span className="text-[11px] text-gray-400 bg-white px-2 py-0.5 rounded-full border border-gray-200 font-medium">
                    Doğrulanmış
                </span>
            </div>

            {/* Contact Channels */}
            <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2.5">
                    İletişim Kanalları
                </h3>
                
                {visibleContacts.length > 0 ? (
                    <div className="space-y-2">
                        {visibleContacts.map(({ key, label, icon: Icon, info, link }) => (
                            <ContactItem
                                key={key}
                                id={key}
                                icon={Icon}
                                label={label}
                                value={info.value}
                                link={link}
                                onCopy={() => copy(info.value, key)}
                                isCopied={copiedKey === key}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="p-5 rounded-2xl bg-gray-50 border border-dashed border-gray-200 text-center text-xs text-gray-500">
                        Araç sahibi iletişim bilgilerini gizli tutmayı tercih etmiştir.
                    </div>
                )}
            </div>

            {/* Direct QR Chat with Vehicle Owner */}
            <div className="pt-2">
                <QrDirectChat qrCodeId={profileData.qrCodeId} profileData={profileData} isOwner={isOwner} />
            </div>
        </div>
    );
};

const CompanyCarView = ({ profileData, isOwner = false }) => {
    const { isRented, companyName, rental } = profileData;
    const [timeRemaining, setTimeRemaining] = React.useState('');
    const [copiedKey, copy] = useCopyToClipboard();

    React.useEffect(() => {
        if (isRented && rental?.rentalEndDate) {
            const interval = setInterval(() => {
                const now = new Date();
                const endDate = rental.rentalEndDate.toDate ? rental.rentalEndDate.toDate() : new Date(rental.rentalEndDate);
                const diff = endDate.getTime() - now.getTime();

                if (diff > 0) {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                    setTimeRemaining(`${hours}sa ${minutes}dk ${seconds}sn kaldı`);
                } else {
                    setTimeRemaining('Kira süresi tamamlandı');
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isRented, rental]);

    const companyPhone = resolveContactField(profileData.companyContactPhone);
    const companyWebsite = resolveContactField(profileData.companyWebsite);
    const companyInstagram = resolveContactField(profileData.companyInstagram);
    const companyWhatsapp = resolveContactField(profileData.companyWhatsapp);

    return (
        <div className="space-y-5">
            {isRented ? (
                <div className="p-4 rounded-2xl bg-red-50/70 border border-red-200/70 text-center">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[11px] font-bold">
                        <FaUser size={10} /> Aktif Kirada
                    </span>
                    {timeRemaining && <p className="text-sm font-bold text-red-900 mt-2">{timeRemaining}</p>}
                    {profileData.renterInfo?.name?.isVisible && profileData.renterInfo?.name?.value && (
                        <p className="text-xs text-gray-700 mt-1">Sürücü: <strong>{profileData.renterInfo.name.value}</strong></p>
                    )}
                </div>
            ) : (
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200/70 text-center">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                        <FaCar size={10} /> Araç Müsait
                    </span>
                    {profileData.availabilityInfo?.note?.isVisible && profileData.availabilityInfo?.note?.value && (
                        <p className="text-xs text-emerald-900 mt-2 font-medium">{profileData.availabilityInfo.note.value}</p>
                    )}
                </div>
            )}

            {/* Company Info Card */}
            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    Filo & Araç Sahibi Şirket
                </span>
                <p className="text-sm font-bold text-gray-900 mb-3">{companyName || 'Rent A Car Firması'}</p>

                <div className="space-y-2">
                    {companyPhone.isVisible && (
                        <ContactItem
                            id="compPhone"
                            icon={FaPhone}
                            label="Yetkili Telefon"
                            value={companyPhone.value}
                            link={formatPhoneLink(companyPhone.value)}
                            onCopy={() => copy(companyPhone.value, 'compPhone')}
                            isCopied={copiedKey === 'compPhone'}
                        />
                    )}
                    {companyWhatsapp.isVisible && (
                        <ContactItem
                            id="compWa"
                            icon={FaWhatsapp}
                            label="WhatsApp"
                            value={companyWhatsapp.value}
                            link={`https://wa.me/${companyWhatsapp.value.replace(/\D/g, '')}`}
                            onCopy={() => copy(companyWhatsapp.value, 'compWa')}
                            isCopied={copiedKey === 'compWa'}
                        />
                    )}
                    {companyWebsite.isVisible && (
                        <ContactItem
                            id="compWeb"
                            icon={FaLink}
                            label="Web Sitesi"
                            value={companyWebsite.value}
                            link={formatWebLink(companyWebsite.value)}
                            onCopy={() => copy(companyWebsite.value, 'compWeb')}
                            isCopied={copiedKey === 'compWeb'}
                        />
                    )}
                    {companyInstagram.isVisible && (
                        <ContactItem
                            id="compInsta"
                            icon={FaInstagram}
                            label="Instagram"
                            value={companyInstagram.value}
                            link={`https://instagram.com/${companyInstagram.value.replace('@', '')}`}
                            onCopy={() => copy(companyInstagram.value, 'compInsta')}
                            isCopied={copiedKey === 'compInsta'}
                        />
                    )}
                </div>
            </div>

            {/* Direct QR Chat with Company Vehicle */}
            <div className="pt-2">
                <QrDirectChat qrCodeId={profileData.qrCodeId} profileData={profileData} isOwner={isOwner} />
            </div>
        </div>
    );
};

const ContactItem = ({ icon: Icon, label, value, link, onCopy, isCopied }) => {
    if (!value) return null;
    return (
        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200/80 hover:border-gray-300 transition-all shadow-xs group">
            <a 
                href={link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-3 text-gray-800 hover:text-blue-600 flex-grow overflow-hidden mr-2 transition-colors"
            >
                <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-blue-50 text-gray-600 group-hover:text-blue-600 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Icon size={14} />
                </div>
                <div className="overflow-hidden">
                    <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider leading-none mb-0.5">{label}</span>
                    <span className="text-xs font-semibold truncate block text-gray-900">{value}</span>
                </div>
            </a>
            
            <button 
                type="button"
                onClick={onCopy} 
                className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                    isCopied 
                        ? 'text-green-700 bg-green-50' 
                        : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`} 
                title="Panoya Kopyala"
                aria-label="Kopyala"
            >
                {isCopied ? <FaCheck size={13} className="text-green-600" /> : <FaCopy size={13} />}
            </button>
        </div>
    );
};

const StatusCard = ({ icon: Icon, color, title, children }) => {
    const colorClasses = {
        amber: 'text-amber-500 bg-amber-50 border-amber-200',
        red: 'text-rose-500 bg-rose-50 border-rose-200',
        blue: 'text-blue-500 bg-blue-50 border-blue-200',
    };
    const style = colorClasses[color] || colorClasses.amber;

    return (
        <div className="text-center py-6 px-4 my-auto">
            <div className={`w-16 h-16 mx-auto rounded-3xl flex items-center justify-center mb-4 border ${style}`}>
                <Icon size={30} />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-1">{title}</h3>
            {children}
        </div>
    );
};

export default QrScanPage;